import {
  ApiGetData,
  ApiPatchData,
  ApiPreference,
  CommunicationOption,
} from 'models/profile-settings.model';

const PREFERENCE_MAP: Record<
  string,
  {
    labelId: string;
    apiName: string;
    sectionId: string;
    type: 'radio' | 'checkbox';
    isBoolean: boolean;
  }
> = {
  'doc-delvry': {
    labelId: 'STR_PAPERLESS',
    apiName: 'doc-delvry',
    sectionId: 'doc-delvry-sec',
    type: 'radio',
    isBoolean: true,
  },
  'info-eml-notf': {
    labelId: 'STR_PERSONALIZED_EMAIL',
    apiName: 'info-eml-notf',
    sectionId: 'info-eml-notf-sec',
    type: 'checkbox',
    isBoolean: true,
  },
  'oper-eml-notf': {
    labelId: 'STR_TIPS_AND_RECOMMENDATIONS',
    apiName: 'oper-eml-notf',
    sectionId: 'oper-eml-notf-sec',
    type: 'checkbox',
    isBoolean: true,
  },
  'prom-eml-notf': {
    labelId: 'STR_PRODUCT_UPDATES',
    apiName: 'prom-eml-notf',
    sectionId: 'prom-eml-notf-sec',
    type: 'checkbox',
    isBoolean: true,
  },
  'txt-msg-notf': {
    labelId: 'STR_TEXT',
    apiName: 'txt-msg-notf',
    sectionId: 'txt-msg-notf-sec',
    type: 'checkbox',
    isBoolean: true,
  },
  'pref-lang': {
    labelId: 'STR_LANGUAGE',
    apiName: 'pref-lang',
    sectionId: 'pref-lang-sec',
    type: 'checkbox',
    isBoolean: true,
  },
};

const getTransformedValue = (
  config: { labelId: string },
  value: boolean | string,
) => {
  if (config.labelId === 'STR_LANGUAGE') {
    return value ? 'ES' : 'EN';
  }
  if (config.labelId === 'STR_PAPERLESS') {
    return value ? 'Y' : 'N';
  }
  return value ? 'Y' : 'N';
};

const extractEmailFromPreferences = (
  apiGetData: ApiGetData | undefined,
): string => {
  const section = apiGetData?.data.find((s) => s.id === 'pref-eml-sec');
  const preference = section?.attributes.preferences.find(
    (p) => p.attributes.options[0].name === 'pref-eml',
  );
  return preference?.attributes.options[0].value || '';
};

const extractPhoneFromPreferences = (
  apiGetData: ApiGetData | undefined,
): string => {
  const section = apiGetData?.data.find((s) => s.id === 'pref-phn-sec');
  const preference = section?.attributes.preferences.find(
    (p) => p.attributes.options[0].name === 'pref-phn',
  );
  return preference?.attributes.options[0].value || '';
};

export const useCommunicationPreferenceMapper = () => {
  const mapApiToOptions = (
    apiGetData: ApiGetData | undefined,
    showPaperlessSetup: boolean,
  ): CommunicationOption[] => {
    const options: CommunicationOption[] = [];
    Object.entries(PREFERENCE_MAP).forEach(([apiName, config]) => {
      if (config.labelId === 'STR_PAPERLESS' && !showPaperlessSetup) {
        return;
      }

      const section = apiGetData?.data.find((s) => s.id === config.sectionId);
      const preference = section?.attributes.preferences.find(
        (p) => p.attributes.options[0].name === apiName,
      );
      const apiValue = preference?.attributes.options[0].value;

      let value: boolean;
      if (config.labelId === 'STR_LANGUAGE') {
        value = apiValue === 'ES';
      } else {
        value = apiValue === 'Y';
      }

      options.push({
        labelId: config.labelId,
        value,
        type: config.type,
        isBoolean: config.isBoolean,
      });
    });

    return options;
  };

  const mapOptionsToApi = (
    options: CommunicationOption[],
    initialOptions: CommunicationOption[],
    email?: string,
  ): ApiPatchData => {
    const preferences: ApiPreference[] = [];

    options.forEach((option) => {
      const initialOption = initialOptions.find(
        (o) => o.labelId === option.labelId,
      );

      if (initialOption && option.value !== initialOption.value) {
        const config = Object.values(PREFERENCE_MAP).find(
          (c) => c.labelId === option.labelId,
        );
        if (config) {
          const preference: ApiPreference = {
            id: config.apiName,
            name: config.apiName,
            options: [],
          };

          let optionName: string;
          if (config.labelId === 'STR_PAPERLESS') {
            optionName = 'print';
            preference.options.push({
              name: optionName,
              value: getTransformedValue(config, option.value),
            });
            if (email) {
              preference.options.push({
                name: 'emailContactPreference',
                value: email,
              });
            }
          } else if (config.labelId === 'STR_LANGUAGE') {
            optionName = 'language';
            preference.options.push({
              name: optionName,
              value: getTransformedValue(config, option.value),
            });
          } else {
            optionName = 'notification';
            preference.options.push({
              name: optionName,
              value: getTransformedValue(config, option.value),
            });
          }

          preferences.push(preference);
        }
      }
    });

    return {
      data: {
        id: 'communication-preferences',
        type: 'communication-preference-data',
        attributes: {
          preferences,
        },
      },
    };
  };

  return {
    mapApiToOptions,
    mapOptionsToApi,
    extractEmailFromPreferences,
    extractPhoneFromPreferences,
  };
};
